import React, { useState } from 'react';
import { Table, Button, Pagination, Icon, Loader, Dimmer } from 'semantic-ui-react';
import styled from 'styled-components';
import { BondInsuranceData } from '../../../services/dashboard';
import * as XLSX from 'xlsx';
import { get } from '../../../services/request';
import { dangerNotification, successNotification } from '../../../services/notification';
import { ReportModal } from '../../../components/ReportModal';
import moment from 'moment-timezone';
import { DateToStringDate } from '../../../services/date';
import { FormatCurrency } from '../../../util';
import SummaryModal from './SummaryModal';

// Estilos
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const TableContainer = styled.div`
  margin-top: 20px;
  overflow-x: auto;
`;

const StyledTable = styled(Table)`
  && {
    font-size: 0.85em;
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 30px;
  color: #666;
`;

interface BondInsuranceTableProps {
  tableData: BondInsuranceData[];
  tableLoading: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalItems: number;
}

const BondInsuranceTable: React.FC<BondInsuranceTableProps> = ({
  tableData,
  tableLoading,
  currentPage,
  setCurrentPage,
  totalItems
}) => {
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [selectedEstate, setSelectedEstate] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportState, setReportState] = useState<number | undefined>(undefined);
  const [isExporting, setIsExporting] = useState(false);

  const itemsPerPage = 10;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handlePageChange = (_: any, { activePage }: any) => {
    setCurrentPage(Number(activePage));
  };

  const handleOpenSummaryModal = (estateId: string, estateName: string) => {
    setSelectedEstate({ id: estateId, name: estateName });
    setIsSummaryModalOpen(true);
  };

  const handleCloseSummaryModal = () => {
    setIsSummaryModalOpen(false);
  };

  const handleExportReport = async () => {
    if (!selectedEstate) {
      return;
    }

    setIsExporting(true);

    try {
      const endDate = moment().format('YYYY-MM-DD');
      const startDate = moment().subtract(30, 'days').format('YYYY-MM-DD');

      const response = await get(
        `/bond-insurance/policies/?isRv=true&isReport=true&status=ALL&start=${startDate}&end=${endDate}&page=1`
      );

      const date = DateToStringDate(new Date());
      const policyHeader = [
        [
          'IMOBILIÁRIA',
          'GESTOR DE CONTAS',
          'LOCATÁRIO',
          'DOCUMENTO',
          'SEGURADORA',
          'PLANO SELECIONADO',
          'EMITIDA EM',
          'CÓDIGO',
          'STATUS DA APÓLICE',
          'DATA HIRING',
          'TOTAL DE PARCELAS',
          'VALOR DA PARCELA',
          'PRÊMIO TOTAL',
          'ALUGUEL',
          'CONDOMÍNIO',
          'GÁS',
          'ÁGUA',
          'ENERGIA',
          'IPTU MENSAL',
          ' ORIGEM ',
          ' - '
        ]
      ];

      const workbook = XLSX.utils.book_new();

      const policySheet = XLSX.utils.json_to_sheet([]);
      XLSX.utils.sheet_add_aoa(policySheet, policyHeader);
      XLSX.utils.sheet_add_json(policySheet, response.policies, {
        origin: 'A2',
        skipHeader: true
      });
      XLSX.utils.book_append_sheet(workbook, policySheet, 'Apólices');

      XLSX.writeFile(workbook, `ReportSeguros-${date}.xlsx`);
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      dangerNotification(
        'Ops...',
        'Erro ao solicitar o relatório. Tente novamente mais tarde.'
      );
    } finally {
      setIsExporting(false);
    }
  };

  const handleReportModalClose = () => {
    setIsReportModalOpen(false);
  };

  return (
    <>
      <Dimmer active={tableLoading} inverted>
        <Loader>Carregando...</Loader>
      </Dimmer>

      <TableContainer>
        <StyledTable celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Imobiliária</Table.HeaderCell>
              <Table.HeaderCell>Franquia</Table.HeaderCell>
              <Table.HeaderCell>Gestor</Table.HeaderCell>
              <Table.HeaderCell>Total Análises</Table.HeaderCell>
              <Table.HeaderCell>Apólices</Table.HeaderCell>
              <Table.HeaderCell>Emitidas</Table.HeaderCell>
              <Table.HeaderCell>Renovadas</Table.HeaderCell>
              <Table.HeaderCell>Aprovadas</Table.HeaderCell>
              <Table.HeaderCell>Prêmios Ativos</Table.HeaderCell>
              <Table.HeaderCell>Ações</Table.HeaderCell>
            </Table.Row>
          </Table.Header>

          <Table.Body>
            {tableLoading ? (
              <Table.Row>
                <Table.Cell colSpan={10} textAlign="center">
                  <Loader active inline="centered" />
                </Table.Cell>
              </Table.Row>
            ) : tableData.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={10}>
                  <NoDataMessage>
                    <Icon name="search" />
                    Nenhum dado encontrado.
                  </NoDataMessage>
                </Table.Cell>
              </Table.Row>
            ) : (
              tableData.map((item, index) => (
                <Table.Row key={`${item.estateId}-${index}`}>
                  <Table.Cell>{item.estateName}</Table.Cell>
                  <Table.Cell>{item.franchiseName}</Table.Cell>
                  <Table.Cell>{item.managerName || 'Não atribuído'}</Table.Cell>
                  <Table.Cell>{item.analysisCount}</Table.Cell>
                  <Table.Cell>{item.policiesCount}</Table.Cell>
                  <Table.Cell>{item.issuedCount}</Table.Cell>
                  <Table.Cell>{item.renewedCount}</Table.Cell>
                  <Table.Cell>{item.approvedCount}</Table.Cell>
                  <Table.Cell>{FormatCurrency(item.totalActivePremiums)}</Table.Cell>
                  <Table.Cell>
                    <Button
                      icon="chart bar"
                      style={{ backgroundColor: '#59249b', color: '#fff' }}
                      onClick={() =>
                        handleOpenSummaryModal(item.estateId, item.estateName)
                      }
                    ></Button>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </StyledTable>
      </TableContainer>

      {!tableLoading && totalPages > 1 && (
        <PaginationContainer>
          <Pagination
            activePage={currentPage}
            onPageChange={handlePageChange}
            totalPages={totalPages}
            ellipsisItem={{ content: <Icon name="ellipsis horizontal" />, icon: true }}
            firstItem={{ content: <Icon name="angle double left" />, icon: true }}
            lastItem={{ content: <Icon name="angle double right" />, icon: true }}
            prevItem={{ content: <Icon name="angle left" />, icon: true }}
            nextItem={{ content: <Icon name="angle right" />, icon: true }}
          />
        </PaginationContainer>
      )}

      {selectedEstate && (
        <SummaryModal
          isOpen={isSummaryModalOpen}
          onClose={handleCloseSummaryModal}
          title={`Resumo de Seguro Fiança - ${selectedEstate.name}`}
          productType="bond_insurance"
          estateId={selectedEstate.id}
          onExport={handleExportReport}
        />
      )}
    </>
  );
};

export default BondInsuranceTable;
